import React from 'react';
import type { FeasibilityCell, FeasibilityResult, QuotaCellProgress } from '../../types';
import { Button, Table, Td, Th, cx } from '../../ui';
import { CellFeasibility } from '../feasibility/FeasibilityNote';
import { CellFill } from './CellFill';
import { CellPicker } from './CellPicker';
import type { PickerGroup } from './CellPicker';
import { looseCompletes, looseHasGender } from './quotasModel';
import type { LooseDimension, LooseRow } from './quotasModel';

const cell = 'w-20 rounded-md border bg-surface px-2 py-1.5 text-right text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-100';
const DIMENSIONS: { value: LooseDimension; label: string }[] = [
  { value: 'age', label: 'Age band' },
  { value: 'region', label: 'Regions' },
  { value: 'profiling', label: 'Profiling' },
  { value: 'none', label: 'Gender only' },
];

interface LooseQuotaTableProps {
  rows: LooseRow[];
  attempted: boolean;
  errors: Record<string, string>;
  serverRowErrors: Record<string, string[]>;
  stateGroups: PickerGroup[];
  answerGroups: PickerGroup[];
  canRegion: boolean;
  canProfiling: boolean;
  onUpdate: (key: string, patch: Partial<LooseRow>) => void;
  onAdd: (dimension: LooseDimension) => void;
  onRemove: (key: string) => void;
  /** the server's count for a row (by position), and the whole answer for the tooltip */
  cellFor?: (index: number) => FeasibilityCell | undefined;
  /** live fill of a saved row (by position); when given, a "Filled" column is shown */
  fillFor?: (index: number) => QuotaCellProgress | undefined;
  result?: FeasibilityResult | null;
}

/**
 * Independent ("non-interlocking") quotas: every row is ONE quota on ONE dimension (an age band, regions, profiling
 * answers, or just a gender split), with an optional male / female split. Rows are not added up and age bands may
 * overlap; the only limit is that no single row may exceed the wanted completes.
 * With a gender split the row's completes are male + female; without one, the Completes box is used.
 */
export function LooseQuotaTable({ rows, attempted, errors, serverRowErrors, stateGroups, answerGroups, canRegion, canProfiling, onUpdate, onAdd, onRemove, cellFor, fillFor, result = null }: LooseQuotaTableProps) {
  const colSpan = fillFor ? 8 : 7;
  return (
    <>
      {rows.length === 0 ? (
        <p className="border-t border-line px-4 py-10 text-center text-sm text-muted">No quotas yet. Add an age band, regions, profiling or a gender split below.</p>
      ) : (
        <Table caption="Independent quotas">
          <thead>
            <tr><Th>Quota on</Th><Th>Applies to</Th><Th numeric>Male</Th><Th numeric>Female</Th><Th numeric>Completes</Th><Th numeric title="Matching panelists. A green tick means there are enough for this row's wanted completes.">Panel</Th>{fillFor && <Th numeric title="Completes so far against this row's wanted completes, from respondents.">Filled</Th>}<Th><span className="sr-only">Remove</span></Th></tr>
          </thead>
          <tbody>
            {rows.map((r, i) => {
              const err = attempted ? errors[r.key] : undefined;
              const serverRow = serverRowErrors[r.key];
              const flagged = Boolean(err || serverRow);
              const label = `row ${i + 1}`;
              const gender = looseHasGender(r);
              const dimension = r.dimension || 'age';
              return (
                <React.Fragment key={r.key}>
                  <tr className={flagged ? 'bg-danger-soft/40' : undefined}>
                    <Td className="py-1.5">
                      <select aria-label={`Quota on, ${label}`} value={dimension} onChange={(e) => onUpdate(r.key, { dimension: e.target.value as LooseDimension })} className="rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-ink">
                        {DIMENSIONS.map((d) => (
                          <option key={d.value} value={d.value} disabled={(d.value === 'region' && !canRegion) || (d.value === 'profiling' && !canProfiling)}>{d.label}</option>
                        ))}
                      </select>
                    </Td>
                    <Td className="py-1.5">
                      {dimension === 'age' && (
                        <span className="inline-flex items-center gap-1.5">
                          <input aria-label={`Age from, ${label}`} inputMode="numeric" className={cx(cell, 'w-16 text-left', flagged && 'border-danger')} value={r.minAge} onChange={(e) => onUpdate(r.key, { minAge: e.target.value })} />
                          <span aria-hidden="true" className="text-muted">–</span>
                          <input aria-label={`Age to, ${label}`} inputMode="numeric" className={cx(cell, 'w-16 text-left', flagged && 'border-danger')} value={r.maxAge} onChange={(e) => onUpdate(r.key, { maxAge: e.target.value })} />
                        </span>
                      )}
                      {dimension === 'region' && <CellPicker label={`Regions, ${label}`} emptyText="Choose regions" groups={stateGroups} selected={r.stateIds} onChange={(ids) => onUpdate(r.key, { stateIds: ids })} />}
                      {dimension === 'profiling' && <CellPicker label={`Profiling, ${label}`} emptyText="Choose answers" groups={answerGroups} selected={r.profilingAnswerIds} onChange={(ids) => onUpdate(r.key, { profilingAnswerIds: ids })} />}
                      {dimension === 'none' && <span className="text-xs text-muted">Everyone</span>}
                    </Td>
                    {([['male', 'Male completes'], ['female', 'Female completes']] as const).map(([f, l]) => (
                      <Td key={f} numeric className="py-1.5"><input aria-label={`${l}, ${label}`} inputMode="numeric" placeholder="—" className={cx(cell, flagged && 'border-danger')} value={r[f]} onChange={(e) => onUpdate(r.key, { [f]: e.target.value } as Partial<LooseRow>)} /></Td>
                    ))}
                    <Td numeric className="py-1.5">
                      {gender
                        ? <span className="font-semibold" aria-label={`Completes, ${label}`}>{looseCompletes(r)}</span>
                        : <input aria-label={`Completes, ${label}`} inputMode="numeric" className={cx(cell, flagged && 'border-danger')} value={r.completes} onChange={(e) => onUpdate(r.key, { completes: e.target.value })} />}
                    </Td>
                    <Td numeric className="py-1.5"><CellFeasibility cell={cellFor ? cellFor(i) : undefined} result={result} /></Td>
                    {fillFor && <Td numeric className="py-1.5"><CellFill cell={fillFor(i)} /></Td>}
                    <Td className="py-1.5 text-right"><Button variant="ghost" size="sm" aria-label={`Remove ${label}`} onClick={() => onRemove(r.key)}>✕</Button></Td>
                  </tr>
                  {err && <tr><td colSpan={colSpan} className="bg-danger-soft px-4 py-1.5 text-xs text-danger" role="alert">Row {i + 1}: {err}</td></tr>}
                  {serverRow && serverRow.map((m) => <tr key={m}><td colSpan={colSpan} className="bg-danger-soft px-4 py-1.5 text-xs text-danger" role="alert">Row {i + 1}: {m}</td></tr>)}
                </React.Fragment>
              );
            })}
          </tbody>
        </Table>
      )}
      <div className="flex flex-wrap items-center gap-1 border-t border-line px-4 py-2.5">
        <span className="mr-1 text-xs text-muted">Add</span>
        <Button variant="ghost" size="sm" onClick={() => onAdd('age')}>＋ Age band</Button>
        <Button variant="ghost" size="sm" onClick={() => onAdd('region')} disabled={!canRegion} title={canRegion ? undefined : 'This target group has no regions saved'}>＋ Regions</Button>
        <Button variant="ghost" size="sm" onClick={() => onAdd('profiling')} disabled={!canProfiling} title={canProfiling ? undefined : 'This target group has no profiling answers saved'}>＋ Profiling</Button>
        <Button variant="ghost" size="sm" onClick={() => onAdd('none')}>＋ Gender split</Button>
      </div>
    </>
  );
}
